import {
  Build as BuildIcon,
  CleaningServicesOutlined as CleaningServicesOutlinedIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  styled,
  svgIconClasses,
  SxProps,
  Theme,
} from '@mui/material';
import classNames from 'classnames';
import { CSSProperties, useMemo } from 'react';

import {
  EAircraftEventStatus,
  ORGAN_COLORS,
  PlaneIcon,
  PlaneOnFlyIcon,
} from '#shared';

import { useAircrafts } from '../../data-context';
import {
  dutyColorFromMs,
  getAirportLabel,
  getCardAccents,
  getCurrentEventLegacy,
  getEventColors,
  getEventTime,
  getTimeOnDutyLabel,
  NEUTRAL,
} from '../../helpers';

import type {
  AircraftCardItem,
  AircraftData,
  AircraftEventLegacy,
  Airport,
  MaoAirport,
} from '#entities';

const STATUS_MAP: Record<EAircraftEventStatus, string> = {
  [EAircraftEventStatus.OnTrip]: 'On Trip',
  [EAircraftEventStatus.OnDuty]: 'Available',
  [EAircraftEventStatus.OnCrewDuty]: 'Available',
  [EAircraftEventStatus.OffDuty]: 'Off Duty',
  [EAircraftEventStatus.OnMaintenance]: 'Maint',
  [EAircraftEventStatus.NotAvailable]: 'Not Available',
};

const EVENT_TYPE_MAP: Record<EAircraftEventStatus, string> = {
  [EAircraftEventStatus.OnTrip]: '',
  [EAircraftEventStatus.OnDuty]: 'On Duty for',
  [EAircraftEventStatus.OnCrewDuty]: 'On Duty for',
  [EAircraftEventStatus.OffDuty]: 'On Rest for',
  [EAircraftEventStatus.OnMaintenance]: 'Maintenance',
  [EAircraftEventStatus.NotAvailable]: 'N/A for',
};

const PREFIX = 'TSMED-AircraftCard';

const aircraftCardClasses = {
  topBlock: `${PREFIX}-topBlock`,
  timer: `${PREFIX}-timer`,
  status: `${PREFIX}-status`,
  iconBlock: `${PREFIX}-iconBlock`,
  eventsBlock: `${PREFIX}-eventsBlock`,
  event: `${PREFIX}-event`,
  eventName: `${PREFIX}-eventName`,
  eventTimer: `${PREFIX}-eventTimer`,
  icon: `${PREFIX}-icon`,
  registration: `${PREFIX}-registration`,
  dutyIcon: `${PREFIX}-dutyIcon`,
  airportsBlock: `${PREFIX}-airportsBlock`,
  airports: `${PREFIX}-airports`,
  airportName: `${PREFIX}-airportName`,
  airportText: `${PREFIX}-airportText`,
  line: `${PREFIX}-line`,
  onMaintenance: `${PREFIX}-onMaintenance`,
};

type RootProps = {
  insideWidget?: boolean;
};
const excludedProps: PropertyKey[] = ['insideWidget'];

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: styles => styles.root,
  shouldForwardProp: p => !excludedProps.includes(p),
})<RootProps>(({
  theme: { utils, breakpoints, spacing, palette },
  insideWidget = false,
}) => {
  const clmp = (min: number, max: number, widgetMax?: number) =>
    insideWidget
      ? utils.clamp(min, widgetMax ?? max)
      : utils.clamp(min, max, 'sm-xl');

  return {
    '--border': '1px',
    '--line-w': insideWidget ? '1px' : utils.clamp(1, 2, 'sm-xl'),
    '--pad': insideWidget ? '8px' : utils.clamp(8, 16),
    '--gap-xs': clmp(2, 5, 4),
    '--gap-sm': clmp(3, 8, 5),
    '--gap-md': clmp(5, 11, 8),
    '--radius': '4px',
    '--icon': clmp(28, 50, 40),
    '--badge-w': clmp(36, 54, 50),
    '--badge-h': clmp(20, 28, 26),
    '--event-h': clmp(20, 35, 35),

    border: `var(--border) solid var(--card-border, #7f8083)`,

    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--radius)',
    padding: 'var(--pad)',
    minHeight: insideWidget ? 'var(--card-min-h, 240px)' : 'auto',

    '&:hover, &:focus-visible': {
      borderColor: 'var(--card-border-hover, var(--card-border, #7f8083))',
      cursor: 'pointer',
    },

    [breakpoints.up('xl')]: insideWidget
      ? {}
      : {
          // Accent the border on xl+ screens
          '--card-border':
            palette.mode === 'dark'
              ? (palette.primary[300] ?? palette.primary.main)
              : (palette.primary[700] ?? palette.primary.main),
          '--card-border-hover': palette.primary.main,
        },

    [breakpoints.up('md')]: insideWidget
      ? {
          height: '100%',
          minHeight: 'var(--card-min-h, 240px)',
          overflow: 'hidden',
        }
      : {
          height: '100%',
          minHeight: 0,
          alignSelf: 'stretch',
        },

    [breakpoints.down('md')]: {
      height: 'auto',
      minHeight: 'auto',
    },

    // At 2K+ in widget mode increase padding so content breathes inside the taller minHeight rows.
    ...(insideWidget && {
      [breakpoints.up('2k')]: {
        '--pad': '10px',
      },
      [breakpoints.up('4k')]: {
        '--pad': '12px',
      },
    }),

    [`&.${aircraftCardClasses.onMaintenance}`]: {
      color: '#83343B',
      '&.MaintenanceType-AOG': { color: '#FF8880' },
    },

    [`.${aircraftCardClasses.topBlock}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--gap-xs)',
      flex: '0 0 auto',
    },

    [`.${aircraftCardClasses.timer},.${aircraftCardClasses.status}`]: {
      fontSize: clmp(14, 24, 18),
      fontWeight: 500,
    },
    [`.${aircraftCardClasses.status}`]: {
      fontFamily: "'JetBrains Mono', monospace",
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    [`.${aircraftCardClasses.iconBlock}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--gap-sm)',
    },

    [`.${aircraftCardClasses.eventsBlock}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--gap-md)',
      minHeight: 0,
      flex: insideWidget ? '1 1 auto' : '0 0 auto',
      flexGrow: 1,
      [breakpoints.down('sm')]: { margin: spacing(1.5, 0) },
    },

    [`.${aircraftCardClasses.event}`]: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: clmp(14, 24, 18),
      fontWeight: 500,
      display: 'flex',
      gap: 'var(--gap-sm)',
      height: insideWidget ? 'var(--event-h)' : 'auto',
      minHeight: insideWidget ? 'var(--event-h)' : 0,
      alignItems: 'center',
    },

    [`.${aircraftCardClasses.eventName}`]: {
      color: 'currentColor',
      flexGrow: 1,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      width: '100%',
      padding: `${clmp(1, 3, 2)} ${clmp(4, 9, 6)}`,
      boxSizing: 'border-box',
    },

    [`.${aircraftCardClasses.eventTimer}`]: {
      fontFeatureSettings: "'zero' 1",
      display: 'flex',
      alignItems: 'center',
      color: 'currentColor',
      flexShrink: 0,
      padding: `${clmp(1, 3, 2)} ${clmp(4, 9, 6)}`,
      boxSizing: 'border-box',
    },

    [`.${aircraftCardClasses.icon}`]: {
      display: 'flex',
      width: 'var(--icon)',
      height: 'var(--icon)',
    },

    [`.${aircraftCardClasses.registration}`]: {
      fontFamily: "'JetBrains Mono', monospace",
      fontFeatureSettings: "'zero' 1",
      fontSize: clmp(14, 28, 18),
      fontWeight: 500,
      display: 'block',
    },

    [`.${aircraftCardClasses.dutyIcon}`]: {
      width: 'var(--badge-w)',
      height: 'var(--badge-h)',
      borderRadius: '4px 0 4px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: clmp(14, 24, 18),
      backgroundColor: '#7f808326',
      [`.${svgIconClasses.root}`]: { color: 'inherit', fontSize: 'inherit' },
    },

    [`.${aircraftCardClasses.airportsBlock}`]: {
      display: 'flex',
      flexDirection: 'column',
      color: '#7f8083',
      flex: '0 0 auto',
      minHeight: 0,
    },
    [`.${aircraftCardClasses.airports}`]: {
      display: 'flex',
      justifyContent: 'space-between',
      minWidth: 0,
      gap: 'var(--gap-sm)',
    },
    [`.${aircraftCardClasses.airportName}`]: {
      fontSize: clmp(12, 20, 14),
      fontWeight: 500,
      display: 'block',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`.${aircraftCardClasses.airportText}`]: {
      fontSize: clmp(12, 24, 14),
      fontWeight: 500,
      minWidth: 0,
    },

    [`.${aircraftCardClasses.line}`]: {
      width: '100%',
      border: 0,
      borderBottom: `var(--line-w) dashed #7f8083`,
    },
  };
});
const getAirportCityState = (airport: Airport | MaoAirport): string => {
  if (!airport) {
    return '';
  }
  if (airport.ServedCityArea) {
    return airport.ServedCityArea.Code;
  }

  return (
    airport.Country?.Name.split(' ')
      .map(i => i[0].toUpperCase())
      .join('') ?? ''
  );
};

const EventsList = ({
  events,
  currentEvent,
  isReturnToBase = false,
}: {
  events: AircraftEventLegacy[];
  currentEvent: AircraftEventLegacy;
  isReturnToBase?: boolean;
}) => (
  <div className={aircraftCardClasses.eventsBlock}>
    {events.map((event, index) => {
      const isCurrent = event.Id === currentEvent.Id;

      const elapsedMs =
        isCurrent && event.Type === EAircraftEventStatus.OnDuty
          ? getEventTime(event.EventTimeMs)
          : 0;

      // Resolve colors
      const { fg, bg } = getEventColors({
        eventType: event.Type,
        isCurrentEvent: isCurrent,
        eventElapsedMs: elapsedMs,
        organName: event.Organ?.name ?? null,
        isReturnToBase,
        organColors: ORGAN_COLORS,
      });

      // all non-first items forced to neutral
      const color = index === 0 ? fg : NEUTRAL;
      const backgroundColor = index === 0 ? bg : alpha(NEUTRAL, 0.15);

      return (
        <div
          key={event.Id}
          className={aircraftCardClasses.event}
          style={{ color }}
        >
          <div
            className={aircraftCardClasses.eventName}
            style={{ backgroundColor }}
          >
            {event.Label ?? EVENT_TYPE_MAP[event.Type]}
          </div>
          <div
            className={aircraftCardClasses.eventTimer}
            style={{ backgroundColor }}
          >
            {event.EventTimeString}
          </div>
        </div>
      );
    })}
  </div>
);

type AircraftCardProps = {
  renderInsideWidget: boolean;
  sx?: SxProps<Theme>;
  onClick: () => void;
} & AircraftCardItem;

export const AirFleetCard = (props: AircraftCardProps) => {
  const { Registration, Events, renderInsideWidget, onClick, sx } = props;
  const aircrafts = useAircrafts();
  const currentEvent = useMemo(() => getCurrentEventLegacy(Events), [Events]);
  const aircraft = useMemo(
    () => aircrafts.find(a => a.Registration === Registration) as AircraftData,
    [aircrafts, Registration],
  );

  if (props.Type === 'OnGround') {
    const { Airport } = props;
    const label = getAirportLabel(Airport?.IATA, Airport?.ICAO, '----');
    const timeOnDutyMs =
      currentEvent.Type === EAircraftEventStatus.OnDuty
        ? getEventTime(currentEvent.EventTimeMs)
        : 0;

    const duty = dutyColorFromMs(timeOnDutyMs);
    const accents = getCardAccents({
      baseColor: duty,
      insideWidget: renderInsideWidget,
    });
    const timeOnDutyLabel = getTimeOnDutyLabel(timeOnDutyMs);

    return (
      <Root
        insideWidget={renderInsideWidget}
        style={
          {
            '--card-border': accents.border,
            '--card-border-hover': accents.hover,
            color: accents.text,
          } as CSSProperties
        }
        onClick={onClick}
        sx={sx}
      >
        <div className={aircraftCardClasses.topBlock}>
          <span className={aircraftCardClasses.status}>
            {STATUS_MAP[currentEvent.Type]}
          </span>
        </div>
        <div className={aircraftCardClasses.iconBlock}>
          <div className={aircraftCardClasses.icon}>
            <PlaneIcon />
          </div>
          <span className={aircraftCardClasses.registration}>
            {Registration}
          </span>
          <div
            className={aircraftCardClasses.dutyIcon}
            style={{ backgroundColor: alpha(duty, 0.15) }}
          >
            {timeOnDutyLabel}
          </div>
        </div>
        <EventsList events={Events} currentEvent={currentEvent} />
        <div className={aircraftCardClasses.airportsBlock}>
          {Airport ? (
            <span className={aircraftCardClasses.airportName}>{`${
              Airport.ServedCity
            }, ${getAirportCityState(Airport)}`}</span>
          ) : (
            <span className={aircraftCardClasses.airportName}>----</span>
          )}
          <div className={aircraftCardClasses.airports}>
            <span className={aircraftCardClasses.airportText}>{label}</span>
            <span className={aircraftCardClasses.airportText}>----</span>
          </div>
        </div>
      </Root>
    );
  }

  if (
    props.Type === 'OnMaintenance' &&
    currentEvent.Type === EAircraftEventStatus.OnMaintenance
  ) {
    const { Airport } = props;
    const label = getAirportLabel(Airport?.IATA, Airport?.ICAO, '----');
    const base = currentEvent.MaintenanceType === 'AOG' ? '#FF8880' : '#FF3844';
    const accents = getCardAccents({
      baseColor: base,
      insideWidget: renderInsideWidget,
    });

    return (
      <Root
        insideWidget={renderInsideWidget}
        className={classNames(aircraftCardClasses.onMaintenance, {
          [`MaintenanceType-${currentEvent.MaintenanceType}`]: Boolean(
            currentEvent.MaintenanceType,
          ),
        })}
        style={
          {
            '--card-border': accents.border,
            '--card-border-hover': accents.hover,
            color: accents.text,
          } as CSSProperties
        }
        onClick={onClick}
      >
        <div className={aircraftCardClasses.topBlock}>
          {currentEvent?.MaintenanceType === 'CLN' ? (
            <span className={aircraftCardClasses.status}>Cleaning</span>
          ) : (
            <span className={aircraftCardClasses.status}>{`${
              STATUS_MAP[currentEvent.Type]
            }${
              currentEvent.MaintenanceType &&
              `: ${currentEvent.MaintenanceType}`
            }
          `}</span>
          )}
        </div>
        <div className={aircraftCardClasses.iconBlock}>
          <div className={aircraftCardClasses.icon}>
            <PlaneIcon />
          </div>
          <span className={aircraftCardClasses.registration}>
            {Registration}
          </span>
          <div className={aircraftCardClasses.dutyIcon}>
            {currentEvent.MaintenanceType === 'CLN' ? (
              <CleaningServicesOutlinedIcon />
            ) : (
              (currentEvent.isActual && <BuildIcon />) || <DateRangeIcon />
            )}
          </div>
        </div>
        <EventsList events={Events} currentEvent={currentEvent} />
        <div className={aircraftCardClasses.airportsBlock}>
          {Airport ? (
            <span className={aircraftCardClasses.airportName}>{`${
              Airport.ServedCity
            }, ${getAirportCityState(Airport)}`}</span>
          ) : (
            <span className={aircraftCardClasses.airportName}>----</span>
          )}
          <div className={aircraftCardClasses.airports}>
            <span className={aircraftCardClasses.airportText}>{label}</span>
            <span className={aircraftCardClasses.airportText}>----</span>
          </div>
        </div>
      </Root>
    );
  }

  if (props.Type === 'OnFly') {
    const { IsOnFly, IsOnCase, IsReturnToBase, Status } = props;

    const ArrivalAirport = aircraft?.airportArrival;
    const DepartureAirport =
      aircraft?.airportDeparture ?? aircraft?.airportLastKnown;
    const departureLabel = getAirportLabel(
      DepartureAirport?.IATA,
      DepartureAirport?.ICAO,
      '----',
    );
    const arrivalLabel = getAirportLabel(
      ArrivalAirport?.IATA,
      ArrivalAirport?.ICAO,
      '----',
    );

    const onFlyColor =
      currentEvent.Organ && !IsReturnToBase
        ? ORGAN_COLORS[currentEvent.Organ.name]
        : NEUTRAL;

    const accents = getCardAccents({
      baseColor: onFlyColor,
      insideWidget: renderInsideWidget,
    });

    return (
      <Root
        insideWidget={renderInsideWidget}
        style={
          {
            '--card-border': accents.border,
            '--card-border-hover': accents.hover,
            color: accents.text,
          } as CSSProperties
        }
        onClick={onClick}
      >
        <div className={aircraftCardClasses.topBlock}>
          <span className={aircraftCardClasses.status}>
            {STATUS_MAP[currentEvent.Type]}
          </span>
        </div>
        <div className={aircraftCardClasses.iconBlock}>
          <div className={aircraftCardClasses.icon}>
            {IsOnFly ? <PlaneOnFlyIcon /> : <PlaneIcon />}
          </div>
          <span className={aircraftCardClasses.registration}>
            {Registration}
          </span>
          {!IsOnCase && <div className={aircraftCardClasses.dutyIcon}>X</div>}
        </div>
        <EventsList
          events={Events}
          isReturnToBase={IsReturnToBase}
          currentEvent={currentEvent}
        />
        <div className={aircraftCardClasses.airportsBlock}>
          <span className={aircraftCardClasses.airportName}>
            {!IsOnFly && DepartureAirport
              ? `${DepartureAirport.ServedCity}, ${getAirportCityState(
                  DepartureAirport,
                )}`
              : Status}
          </span>
          <div className={aircraftCardClasses.airports}>
            <span className={aircraftCardClasses.airportText}>
              {departureLabel}
            </span>
            <span className={aircraftCardClasses.airportText}>
              {arrivalLabel}
            </span>
          </div>
        </div>
      </Root>
    );
  }

  return null;
};
